import useAppSelector from './useAppSelector'
import { selectForm } from '../slices/formSlice'
import { RefObject, useCallback, useRef } from 'react'
import { asBlobURL, toFile } from '../utils/File'
import { isMobile } from 'react-device-detect'

function fillBackgroundPattern(canvas: HTMLCanvasElement, edgeLength = 100) {
    const context = canvas.getContext('2d')
    if (!context) return
    const rows = Math.ceil(canvas.height / edgeLength)
    const cols = Math.ceil(canvas.width / edgeLength)
    for (let i = 0; i < rows; i++) {
        for (let j = 0; j < cols; j++) {
            if ((i * cols + j) % 2) {
                context.fillStyle = '#FFFFFF'
            } else {
                context.fillStyle = '#FFFFFF'
            }
            context?.fillRect(i * edgeLength, j * edgeLength, edgeLength, edgeLength)
        }
    }

    context.fillStyle = '#FFFFFF'
    context.font = '36px sans-serif'
    context.textAlign = 'center'

    context.textBaseline = 'bottom'
    context.fillText('Da ist wohl etwas schief gelaufen!', canvas.width / 2, canvas.height / 2)
    context.textBaseline = 'top'
    context.fillText('Bitte versuche es mit einem anderen Browser erneut.', canvas.width / 2, canvas.height / 2)
}

function toSvgDataURL(svg: SVGSVGElement) {
    const serializer = new XMLSerializer()
    const copySVG = svg.cloneNode(true) as SVGSVGElement
    copySVG.setAttribute('width', '1080')
    copySVG.setAttribute('height', '1080')
    const source = serializer.serializeToString(copySVG)
    const url = 'data:image/svg+xml;base64,' + window.btoa(window.unescape(encodeURIComponent(source)))

    return url
}
function toCanvas(
    svgSource: string,
    canvas?: HTMLCanvasElement,
    pos?: { x: number; y: number },
): Promise<HTMLCanvasElement> {
    return new Promise((resolve) => {
        const image = new Image()
        image.crossOrigin = 'Anonymous'
        image.addEventListener('load', function () {
            // Don't ask why... This prevents the "empty image" bug on iOS.
            window.setTimeout(() => {
                const canvas2 = canvas ?? document.createElement('canvas')
                if (!canvas) {
                    canvas2.width = 1080
                    canvas2.height = 1080
                    fillBackgroundPattern(canvas2)
                }

                const context = canvas2.getContext('2d')
                context?.drawImage(image, pos?.x ?? 0, pos?.y ?? 0)

                resolve(canvas2)
            }, 1)
        })
        image.src = svgSource
    })
}
async function toDataURL(
    svgSource: string,
    dataType: string,
    canvas?: HTMLCanvasElement,
    pos?: { x: number; y: number },
): Promise<string> {
    if (dataType === 'image/svg+xml') {
        return Promise.resolve(svgSource)
    } else {
        const canvas2 = await toCanvas(svgSource, canvas, pos)
        return canvas2.toDataURL(dataType)
    }
}

function normalizeFilename(filename: string) {
    return filename.toLowerCase().replace(/[^a-z0-9_]+/g, '-')
}

const triggerFileDownload = async (file: File) => {
    const link = document.createElement('a')
    link.download = file.name
    link.href = await asBlobURL(file).then((blob) => blob.content)
    link.click()
}

const shareFile = async (file: File) => {
    return window.navigator.share({
        files: [file],
    })
}

function useExport(type: string = 'image/png') {
    const { name } = useAppSelector(selectForm)
    const svgRef1 = useRef<SVGSVGElement>(null)
    const svgRef2 = useRef<SVGSVGElement>(null)

    return {
        svgRef1,
        svgRef2,
        download: useCallback(
            async (filename?: string, refs: (undefined | RefObject<SVGSVGElement>)[] = [svgRef1, svgRef2]) => {
                const downloads = refs.map(async (ref, i) => {
                    if (!ref?.current) {
                        return false
                    }
                    const svg = ref.current

                    const svgDataUrl = toSvgDataURL(svg)
                    const file = await toDataURL(svgDataUrl, type).then(
                        toFile(normalizeFilename(filename || `meinezukunftmeinewahl-${name}-${i + 1}`) + '.png'),
                    )
                    if (isMobile && navigator.canShare && navigator.canShare({ files: [file] })) {
                        await shareFile(file)
                    } else {
                        await triggerFileDownload(file)
                    }

                    return true
                })
                await Promise.all(downloads)
            },
            [type, name],
        ),
        export: useCallback(
            async () =>
                Promise.all(
                    [svgRef1.current, svgRef2.current].map(async (svgRef) => {
                        if (!svgRef) {
                            return undefined
                        }

                        const svgDataUrl = toSvgDataURL(svgRef)
                        const dataUrl = await toDataURL(svgDataUrl, type)

                        return dataUrl
                    }),
                ),
            [type],
        ),
    }
}

export default useExport
